import { RefObject, useCallback, useEffect, useRef } from 'react';

/**
 * Hook to enable drag-to-scroll functionality on desktop
 * Allows users to click and drag to scroll horizontally
 */
export const useDragScroll = (containerRef: RefObject<HTMLElement | null>) => {
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!containerRef.current) return;

      isDraggingRef.current = true;
      startXRef.current = e.pageX - containerRef.current.offsetLeft;
      scrollLeftRef.current = containerRef.current.scrollLeft;
      containerRef.current.style.cursor = 'grabbing';
      containerRef.current.style.userSelect = 'none';
    },
    [containerRef],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      e.preventDefault();
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startXRef.current) * 1.5;
      containerRef.current.scrollLeft = scrollLeftRef.current - walk;
    },
    [containerRef],
  );

  const handleMouseUp = useCallback(() => {
    if (!containerRef.current) return;

    isDraggingRef.current = false;
    containerRef.current.style.cursor = 'grab';
    containerRef.current.style.userSelect = '';
  }, [containerRef]);

  const handleMouseLeave = useCallback(() => {
    if (!containerRef.current) return;

    isDraggingRef.current = false;
    containerRef.current.style.cursor = 'grab';
    containerRef.current.style.userSelect = '';
  }, [containerRef]);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!containerRef.current) return;

      const isScrollable =
        containerRef.current.scrollWidth > containerRef.current.clientWidth;

      if (!isScrollable) return;

      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        containerRef.current.scrollLeft += e.deltaY;
      }
    },
    [containerRef],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.style.cursor = 'grab';

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleWheel,
  ]);
};
